import { Popover as BasePopover } from '@base-ui/react/popover';
import type { CSSProperties, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { popupPlacement, type PopupPlacement, type PopupPositionMethod, type VirtualElement } from './popupPlacement';

type PopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | VirtualElement | null;
  onClose: () => void;
  children: ReactNode;
  placement?: PopupPlacement;
  offset?: [number, number];
  className?: string;
  /** Applied to the positioner (the element that can intercept clicks). */
  positionerClassName?: string;
  showBackdrop?: boolean;
  strategy?: PopupPositionMethod;
  sameWidth?: boolean;
  style?: CSSProperties;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  popoverRef?: React.Ref<HTMLDivElement>;
  /** Accepted for call-site compatibility; Base UI popover has no caret. */
  showArrow?: boolean;
};

function eventTargetIsInAnchor(anchorEl: HTMLElement | VirtualElement | null, event: Event | undefined) {
  const target = event?.target;
  return anchorEl instanceof HTMLElement && target instanceof Node && anchorEl.contains(target);
}

export default function Popover({
  open,
  anchorEl,
  onClose,
  children,
  placement = 'bottom',
  offset = [0, 8],
  className = '',
  positionerClassName = '',
  showBackdrop = false,
  strategy = 'fixed',
  sameWidth = false,
  style,
  onMouseEnter,
  onMouseLeave,
  popoverRef
}: PopoverProps) {
  const { side, align } = popupPlacement(placement);
  const [skidding, distance] = offset;
  const anchorWidth = sameWidth && anchorEl instanceof HTMLElement ? anchorEl.offsetWidth : undefined;

  return (
    <BasePopover.Root
      open={open && anchorEl != null}
      modal={showBackdrop}
      onOpenChange={(next, eventDetails) => {
        if (next) return;
        if (eventTargetIsInAnchor(anchorEl, eventDetails.event)) return;
        const target = eventDetails.event?.target;
        if (target instanceof Element && target.closest('[role="dialog"], [aria-modal="true"]')) {
          return;
        }
        onClose();
      }}
    >
      <BasePopover.Portal>
        {showBackdrop && <BasePopover.Backdrop className="fixed inset-0 z-40" />}
        <BasePopover.Positioner
          anchor={anchorEl}
          side={side}
          align={align}
          alignOffset={skidding}
          sideOffset={distance}
          positionMethod={strategy}
          collisionPadding={8}
          className={twMerge('z-critical', positionerClassName)}
        >
          <BasePopover.Popup
            ref={popoverRef}
            data-popover-portal="true"
            initialFocus={(openType) => openType === 'keyboard'}
            onClick={(event) => event.stopPropagation()}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={twMerge(
              'z-critical bg-white border-[2px] border-border-default rounded-lg shadow-md outline-none',
              className
            )}
            style={{
              ...(anchorWidth ? { width: anchorWidth, minWidth: anchorWidth } : {}),
              ...style
            }}
          >
            {children}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}
